import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSocket } from './SocketContext';
import { useAuth } from './AuthContext';
import { notificationService, Notification, NotificationStats } from '../services/notificationService';
import { browserNotificationService } from '../utils/browserNotifications';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  stats: NotificationStats | null;
  isLoading: boolean;
  error: string | null;
  browserNotificationsEnabled: boolean;
  
  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
  requestNotificationPermission: () => Promise<boolean>;
  
  // Real-time updates
  addNotification: (notification: Notification) => void;
  updateNotificationCount: (count: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState<boolean>(false);

  const { socket, isConnected } = useSocket();
  const { state: authState } = useAuth();

  // Load initial notifications
  const loadNotifications = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await notificationService.getNotifications({
        page: pageNum,
        limit: 20,
      });

      if (append) {
        setNotifications(prev => [...prev, ...response.data.notifications]);
      } else {
        setNotifications(response.data.notifications);
      }

      setUnreadCount(response.data.unreadCount);
      setHasMore(response.data.notifications.length === 20);
      setPage(pageNum);
    } catch (err: any) {
      setError(err.message || 'Failed to load notifications');
      console.error('Error loading notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load notification stats
  const loadStats = useCallback(async () => {
    try {
      const statsData = await notificationService.getStats();
      setStats(statsData);
    } catch (err: any) {
      console.error('Error loading notification stats:', err);
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, isRead: true, readAt: new Date().toISOString() }
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Update stats
      if (stats) {
        setStats(prev => prev ? { ...prev, unreadCount: Math.max(0, prev.unreadCount - 1) } : null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mark notification as read');
      console.error('Error marking notification as read:', err);
    }
  }, [stats]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true, readAt: new Date().toISOString() }))
      );
      
      setUnreadCount(0);
      
      // Update stats
      if (stats) {
        setStats(prev => prev ? { ...prev, unreadCount: 0 } : null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mark all notifications as read');
      console.error('Error marking all notifications as read:', err);
    }
  }, [stats]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Update local state
      const deletedNotification = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(notification => notification._id !== notificationId));
      
      // Update unread count if the deleted notification was unread
      if (deletedNotification && !deletedNotification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Update stats
        if (stats) {
          setStats(prev => prev ? { ...prev, unreadCount: Math.max(0, prev.unreadCount - 1) } : null);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete notification');
      console.error('Error deleting notification:', err);
    }
  }, [notifications, stats]);

  // Refresh notifications
  const refreshNotifications = useCallback(async () => {
    setPage(1);
    setHasMore(true);
    await loadNotifications(1, false);
    await loadStats();
  }, [loadNotifications, loadStats]);

  // Load more notifications
  const loadMoreNotifications = useCallback(async () => {
    if (!hasMore || isLoading) return;
    
    const nextPage = page + 1;
    await loadNotifications(nextPage, true);
  }, [hasMore, isLoading, page, loadNotifications]);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async (): Promise<boolean> => {
    try {
      console.log('🔔 Requesting notification permission...');
      const permission = await browserNotificationService.requestPermission();
      console.log('🔔 Permission result:', permission);
      setBrowserNotificationsEnabled(permission.granted);
      return permission.granted;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  // Add new notification (for real-time updates)
  const addNotification = useCallback((notification: Notification) => {
    setNotifications(prev => [notification, ...prev]);
    
    if (!notification.isRead) {
      setUnreadCount(prev => prev + 1);
      
      // Update stats
      if (stats) {
        setStats(prev => prev ? { ...prev, unreadCount: prev.unreadCount + 1 } : null);
      }

      // Show browser notification if enabled
      const permission = browserNotificationService.getPermission();
      if (!permission.granted) {
        // Proactively request once when first unread notification arrives and permission not granted
        requestNotificationPermission();
      }

      if (browserNotificationsEnabled || permission.granted) {
        console.log('🔔 Attempting to show browser notification:', {
          notification,
          browserNotificationsEnabled,
          documentHidden: document.hidden,
          permission
        });

        // Determine notification type and show appropriate browser notification
        if (notification.type === 'message' || notification.type === 'message_received') {
          // Extract contact name from title (remove "New message from " prefix)
          const contactName = notification.title.replace(/^New message from /, '');
          browserNotificationService.showMessageNotification(
            contactName,
            notification.message,
            notification.relatedEntities?.contactId
          );
        } else if (notification.type === 'user_status') {
          const status = notification.message.includes('online') ? 'online' : 'offline';
          browserNotificationService.showUserStatusNotification(notification.title, status);
        } else if (notification.type === 'message_status') {
          const status = notification.message.includes('delivered') ? 'delivered' :
                        notification.message.includes('read') ? 'read' : 'failed';
          browserNotificationService.showMessageStatusNotification(status, notification.title);
        } else {
          browserNotificationService.showSystemNotification(
            notification.title,
            notification.message,
            notification.type
          );
        }
      } else {
        console.log('🔔 Browser notifications not enabled:', {
          browserNotificationsEnabled,
          permission: browserNotificationService.getPermission()
        });
      }
    }
  }, [stats, browserNotificationsEnabled]);

  // Update notification count (for real-time updates)
  const updateNotificationCount = useCallback((count: number) => {
    setUnreadCount(count);
    
    // Update stats
    if (stats) {
      setStats(prev => prev ? { ...prev, unreadCount: count } : null);
    }
  }, [stats]);

  // Socket event handlers
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    // Listen for new notifications
    const handleNewNotification = (notification: Notification) => {
      console.log('📢 New notification received via websocket:', notification);
      addNotification(notification);
    };

    // Listen for notification count updates
    const handleNotificationCount = (data: { unreadCount: number }) => {
      console.log('📊 Notification count updated via websocket:', data.unreadCount);
      updateNotificationCount(data.unreadCount);
    };

    // Add listeners
    socket.on('new_notification', handleNewNotification);
    socket.on('notification_count', handleNotificationCount);

    return () => {
      socket.off('new_notification', handleNewNotification);
      socket.off('notification_count', handleNotificationCount);
    };
  }, [socket, isConnected, addNotification, updateNotificationCount]);

  // Listen for custom events from SocketContext
  useEffect(() => {
    const handleNewNotificationEvent = (event: CustomEvent) => {
      addNotification(event.detail);
    };

    const handleNotificationCountEvent = (event: CustomEvent) => {
      updateNotificationCount(event.detail.unreadCount);
    };

    window.addEventListener('newNotification', handleNewNotificationEvent as EventListener);
    window.addEventListener('notificationCountUpdate', handleNotificationCountEvent as EventListener);

    return () => {
      window.removeEventListener('newNotification', handleNewNotificationEvent as EventListener);
      window.removeEventListener('notificationCountUpdate', handleNotificationCountEvent as EventListener);
    };
  }, [addNotification, updateNotificationCount]);

  // Load initial data only when user is authenticated
  useEffect(() => {
    if (!authState.isAuthenticated || authState.isLoading) {
      return;
    }

    const initializeNotifications = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await notificationService.getNotifications({
          page: 1,
          limit: 20,
        });

        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unreadCount);
        setHasMore(response.data.notifications.length === 20);
        setPage(1);

        // Load stats
        const statsData = await notificationService.getStats();
        setStats(statsData);
      } catch (err: any) {
        setError(err.message || 'Failed to load notifications');
        console.error('Error loading notifications:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeNotifications();
  }, [authState.isAuthenticated, authState.isLoading]); // Only run when auth state changes

  // Auto-refresh notifications every 2 minutes (only when authenticated)
  useEffect(() => {
    if (!isConnected || !authState.isAuthenticated) return;

    const interval = setInterval(async () => {
      try {
        const response = await notificationService.getNotifications({
          page: 1,
          limit: 20,
        });

        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unreadCount);
        setHasMore(response.data.notifications.length === 20);
        setPage(1);
      } catch (err: any) {
        console.error('Error refreshing notifications:', err);
      }
    }, 120000); // 2 minutes instead of 30 seconds

    return () => clearInterval(interval);
  }, [isConnected, authState.isAuthenticated]); // Only when connected and authenticated

  // Reset notification state when user logs out
  useEffect(() => {
    if (!authState.isAuthenticated && !authState.isLoading) {
      setNotifications([]);
      setUnreadCount(0);
      setStats(null);
      setError(null);
      setPage(1);
      setHasMore(true);
    }
  }, [authState.isAuthenticated, authState.isLoading]);

  // Check browser notification permission on mount
  useEffect(() => {
    const permission = browserNotificationService.getPermission();
    setBrowserNotificationsEnabled(permission.granted);
    if (!permission.granted && !permission.denied) {
      // Auto-prompt if user hasn't made a choice yet
      requestNotificationPermission();
    }
  }, [requestNotificationPermission]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    stats,
    isLoading,
    error,
    browserNotificationsEnabled,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    loadMoreNotifications,
    requestNotificationPermission,
    addNotification,
    updateNotificationCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
